/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { TableHorizontalScrollPosition, TableVerticalScrollPosition } from '#UNSAFE_TableView';
import type {
  OverflowInfo,
  SizingInfo,
  StickyColumnInfo,
  TableColumn,
  ViewportInfo
} from '../Table';

import { useCallback, useLayoutEffect, useRef } from 'preact/hooks';
import { isHTMLScroller } from '#PRIVATE_TableCommon/utils/ScrollUtils';
import {
  hideOutOfViewTooltip,
  setHorizontalScrollPosition,
  setVerticalScrollPosition,
  updateStickyEdges
} from '../utils/TableScrollUtils';

type UseScrollHandlingProps<K, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  tableId: string;
  isLayoutRequired: boolean;
  sizingInfoRef: MutableRef<SizingInfo>;
  overflowInfoRef: MutableRef<OverflowInfo>;
  columnsArray: TableColumn<K, D, C>[];
  stickyColumnInfo: StickyColumnInfo;
  stickyEdgesState: { start?: number; end?: number };
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void;
  fullColumnWidths?: number[];
  horizontalScrollPositionOverride?: TableHorizontalScrollPosition<C>;
  verticalScrollPositionOverride?: TableVerticalScrollPosition<K>;
  tooltipAnchorRef?: MutableRef<HTMLElement | undefined>;
  hideTooltip?: (isImmediate?: boolean) => void;
  isPendingLayout: boolean;
  isRtl: boolean;
};

/**
 * Hook that manages handling scroll events on the Preact Table
 */
export function useScrollHandling<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  tableId,
  isLayoutRequired,
  sizingInfoRef,
  overflowInfoRef,
  columnsArray,
  stickyColumnInfo,
  stickyEdgesState,
  setStickyEdgesState,
  fullColumnWidths,
  horizontalScrollPositionOverride,
  verticalScrollPositionOverride,
  tooltipAnchorRef,
  hideTooltip,
  isPendingLayout,
  isRtl
}: UseScrollHandlingProps<K, D, C>) {
  const horizontalScrollPositionOverrideRef = useRef<TableHorizontalScrollPosition<C>>();
  const verticalScrollPositionOverrideRef = useRef<TableVerticalScrollPosition<K>>();

  // update horizontal and/or vertical scroll position if a new value is provided
  // NO DEPENDENCY ARRAY AS THIS SHOULD RUN ON EVERY RENDER
  useLayoutEffect(() => {
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (
      rootElement != null &&
      scrollerElement != null &&
      !isPendingLayout &&
      !isLayoutRequired &&
      fullColumnWidths != null
    ) {
      if (horizontalScrollPositionOverrideRef.current !== horizontalScrollPositionOverride) {
        setHorizontalScrollPosition(
          rootElement,
          scrollerElement,
          viewportInfo,
          sizingInfoRef.current,
          overflowInfoRef.current,
          columnsArray,
          fullColumnWidths,
          isRtl,
          horizontalScrollPositionOverride
        );
        horizontalScrollPositionOverrideRef.current = horizontalScrollPositionOverride;
      }
      if (verticalScrollPositionOverrideRef.current !== verticalScrollPositionOverride) {
        setVerticalScrollPosition(
          rootElement,
          scrollerElement,
          viewportInfo,
          tableId,
          verticalScrollPositionOverride
        );
        verticalScrollPositionOverrideRef.current = verticalScrollPositionOverride;
      }
    }
  });

  // Scroll hander for the outer Table. It ensures the Table's sticky edges are updated
  // and any out of view tooltips are hidden.
  const _scrollHandler = useCallback(() => {
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (rootElement != null && scrollerElement != null && fullColumnWidths != null) {
      updateStickyEdges(
        rootElement,
        scrollerElement,
        viewportInfo,
        columnsArray,
        stickyColumnInfo,
        sizingInfoRef.current,
        overflowInfoRef.current,
        fullColumnWidths,
        isRtl,
        stickyEdgesState,
        setStickyEdgesState
      );
      hideOutOfViewTooltip(
        rootElement,
        scrollerElement,
        viewportInfo,
        tableId,
        stickyColumnInfo,
        sizingInfoRef.current,
        overflowInfoRef.current,
        fullColumnWidths,
        isRtl,
        tooltipAnchorRef,
        hideTooltip
      );
    }
  }, [
    rootRef,
    viewportInfo,
    tableId,
    sizingInfoRef,
    overflowInfoRef,
    columnsArray,
    stickyColumnInfo,
    stickyEdgesState,
    setStickyEdgesState,
    fullColumnWidths,
    tooltipAnchorRef,
    hideTooltip,
    isRtl
  ]);

  useLayoutEffect(() => {
    const isExternalScroller = viewportInfo.isExternalScroller;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    let scrollerEventElement: Element | typeof globalThis;
    if (isExternalScroller && scrollerElement != null) {
      if (isHTMLScroller(scrollerElement)) {
        scrollerEventElement = window;
      } else {
        scrollerEventElement = scrollerElement;
      }
      scrollerEventElement.addEventListener('scroll', _scrollHandler);
    }
    return () => {
      if (isExternalScroller && scrollerEventElement != null) {
        scrollerEventElement.removeEventListener('scroll', _scrollHandler);
      }
    };
  }, [viewportInfo, _scrollHandler]);

  return viewportInfo.isExternalScroller ? {} : { onScroll: _scrollHandler };
}
